import { useState, useCallback, useRef } from "react";
import { sharePost } from "../APIs/shareApi";
import { trackEvent } from "../utils/analytics";

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.picprofile.com';

const useShare = (postId) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareCount, setShareCount] = useState(0);
  const [loading] = useState(false);

  // OG endpoint — crawlers get rich previews, browsers get redirected to frontend
  const postUrl = `${API_BASE}/post/${postId}/`;
  const shareTextRef = useRef('');

  const closeModal = useCallback(() => {
    setOpen(false);
    setCopied(false);
  }, []);

  const recordShare = useCallback(
    async (platform) => {
      try {
        await sharePost(postId, platform);
        setShareCount((prev) => prev + 1);
      } catch (err) {
        console.error("Share record failed:", err);
      }
    },
    [postId]
  );

  // Always open the custom sheet — never native first
  const openShare = useCallback((caption) => {
    shareTextRef.current = caption
      ? `Check this out on PicProfile — ${caption.slice(0, 40)}${caption.length > 40 ? '...' : ''}`
      : 'Check this out on PicProfile';
    setOpen(true);
  }, []);

  // "More apps" button — native share as last resort; falls back to copy link
  const handleMoreApps = useCallback(async () => {
    const text = shareTextRef.current || 'Check this out on PicProfile';
    if (navigator.share) {
      try {
        await navigator.share({ title: 'PicProfile', text, url: postUrl });
        await recordShare('native');
        trackEvent('share_action', { postId });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Native sheet failed — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }, [postUrl, postId, recordShare]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      await recordShare("copy");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  }, [postUrl, recordShare]);

  const handlePlatformShare = useCallback(
    async (platform, url) => {
      window.open(url, "_blank", "noopener,noreferrer");
      await recordShare(platform);
      trackEvent('share_action', { postId });
      closeModal();
    },
    [recordShare, closeModal]
  );

  return {
    open,
    copied,
    shareCount,
    loading,
    postUrl,
    openShare,
    closeModal,
    handleCopy,
    handlePlatformShare,
    handleMoreApps,
  };
};

export default useShare;
